/**
 * Thin Snowplow + Datadog helpers for Desktop Remote/offline consult-note
 * analytics (HAR-1777). Driven only by `session_engine:consult_note`
 * lifecycle → FE listener — not the web/SBS stream path.
 */
import { getChronicleSessionSourceTypeForSession } from '@/atoms/chronicle/session-device-files';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type {
  NoteGenerationAnalyticsSource,
  NoteGenerationCompletedEventProps,
  NoteGenerationFailedEventProps,
  NoteGenerationStartedEventProps,
  NoteGenerationTrigger,
} from '@/models/analytics/features/notes';
import {
  logHardwareDatadogEvent,
  logSessionPipelineDatadogEvent,
  type HardwareDatadogAttributes,
  type HardwareDatadogLogLevel,
} from '@/utils/analytics/log-hardware-datadog-event';
import { SESSION_SOURCE_TYPE } from '@/utils/analytics/session-source-type';
import { logger } from '@/utils/logger/logger';

const noteGenerationAnalyticsOptions = {
  amplitude: false,
} as const;

/**
 * Chronicle consult-note Datadog facet from the device-file registry.
 * On a miss, omit `session_source_type` rather than guessing `bulk_sync` —
 * interrupted-live recovery and prior-app-run sessions can share
 * `TaskSource::Chronicle` and a wrong default is harder to detect than an
 * absent facet (HAR-1777 review).
 */
function chronicleSessionSourceTypeForDatadog(
  sessionId: string | undefined
):
  | typeof SESSION_SOURCE_TYPE.BulkSync
  | typeof SESSION_SOURCE_TYPE.ChronicleLiveRecording
  | undefined {
  // No session id → nothing to look up; skip the miss log entirely.
  if (sessionId === undefined || sessionId === '') {
    return undefined;
  }
  const fromRegistry = getChronicleSessionSourceTypeForSession(sessionId);
  if (
    fromRegistry === SESSION_SOURCE_TYPE.BulkSync ||
    fromRegistry === SESSION_SOURCE_TYPE.ChronicleLiveRecording
  ) {
    return fromRegistry;
  }
  // Miss is a documented steady state (in-memory, session-scoped registry) —
  // info, not warn, so on-call doesn't treat prior-run / interrupted-live gaps
  // as anomalies.
  logger.info({
    msg: '[note_generation_analytics] chronicle session_source_type registry miss — omit facet',
    sessionId,
  });
  return undefined;
}

/**
 * Map Snowplow prop names onto the session-pipeline Datadog facet keys used by
 * neighbouring offline/Chronicle funnel logs (`session_id_local`, `error_type`,
 * `attempt_count`, `total_elapsed_ms`).
 */
function toDatadogNoteGenerationAttrs(attrs: {
  session_id?: string | null;
  local_session_id?: string | null;
  platform?: string;
  device_file_name?: string;
  generation_trigger?: string;
  source?: string;
  note_generation_start_time?: number;
  note_generation_end_time?: number;
  /** Whole-ladder wall elapsed — Datadog key is `total_elapsed_ms`. */
  duration_ms?: number;
  /** Snowplow `error_code` / coarse transport — Datadog key is `error_type`. */
  error_type?: string | null;
  attempt_count?: number | null;
  will_retry?: boolean | null;
}): HardwareDatadogAttributes {
  return {
    session_id: attrs.session_id ?? undefined,
    session_id_local: attrs.local_session_id ?? undefined,
    platform: attrs.platform,
    device_file_name: attrs.device_file_name,
    generation_trigger: attrs.generation_trigger,
    source: attrs.source,
    note_generation_start_time: attrs.note_generation_start_time,
    note_generation_end_time: attrs.note_generation_end_time,
    total_elapsed_ms: attrs.duration_ms,
    error_type: attrs.error_type ?? undefined,
    attempt_count: attrs.attempt_count ?? undefined,
    will_retry: attrs.will_retry ?? undefined,
  };
}

function mirrorToDatadog(
  eventName: string,
  attributes: HardwareDatadogAttributes,
  source: NoteGenerationAnalyticsSource | undefined,
  level: HardwareDatadogLogLevel = 'info'
): void {
  if (source === 'chronicle') {
    // Let cleanAttributes derive session_origin from session_source_type —
    // never hand-stamp origin (session-source-type.ts).
    const sessionId =
      typeof attributes.session_id === 'string' ? attributes.session_id : undefined;
    const session_source_type = chronicleSessionSourceTypeForDatadog(sessionId);
    logHardwareDatadogEvent(
      eventName,
      {
        ...attributes,
        ...(session_source_type !== undefined ? { session_source_type } : {}),
      },
      level
    );
    return;
  }
  if (source === 'offline_recording') {
    logSessionPipelineDatadogEvent(
      eventName,
      {
        ...attributes,
        session_source_type: SESSION_SOURCE_TYPE.OfflineRecording,
      },
      level
    );
    return;
  }
  // Never default unknown / web-only sources to OfflineRecording.
  logger.warn({
    msg: '[note_generation_analytics] unknown source — skip Datadog mirror',
    eventName,
    source,
  });
}

export function trackNoteGenerationStartedAnalytics(
  props: NoteGenerationStartedEventProps & {
    /**
     * Offline sync-queue attempt count — Datadog facet `attempt_count` only.
     * Never stamp sync retries as Snowplow `attempt_number` (iOS generation counter).
     */
    attempt_count?: number | null;
  }
): void {
  const { attempt_count, ...snowplowProps } = props;
  track({
    event_name: TrackingEventName.NoteGenerationStarted,
    props: snowplowProps,
    options: noteGenerationAnalyticsOptions,
  });
  mirrorToDatadog(
    TrackingEventName.NoteGenerationStarted,
    toDatadogNoteGenerationAttrs({
      session_id: props.session_id,
      local_session_id: props.local_session_id,
      platform: props.platform,
      device_file_name: props.device_file_name,
      generation_trigger: props.generation_trigger,
      source: props.source,
      note_generation_start_time: props.note_generation_start_time,
      attempt_count: attempt_count ?? props.attempt_number,
    }),
    props.source
  );
}

export function trackNoteGenerationCompletedAnalytics(
  props: NoteGenerationCompletedEventProps & {
    /**
     * Offline sync-queue attempt count — Datadog facet `attempt_count` only.
     * Never stamp sync retries as Snowplow `attempt_number`.
     */
    attempt_count?: number | null;
  }
): void {
  const { attempt_count, ...snowplowProps } = props;
  track({
    event_name: TrackingEventName.NoteGenerationCompleted,
    props: snowplowProps,
    options: noteGenerationAnalyticsOptions,
  });
  mirrorToDatadog(
    TrackingEventName.NoteGenerationCompleted,
    toDatadogNoteGenerationAttrs({
      session_id: props.session_id,
      local_session_id: props.local_session_id,
      platform: props.platform,
      device_file_name: props.device_file_name,
      duration_ms: props.duration_ms,
      generation_trigger: props.generation_trigger,
      source: props.source,
      note_generation_end_time: props.note_generation_end_time,
      attempt_count: attempt_count ?? props.attempt_number,
    }),
    props.source
  );
}

export function trackNoteGenerationFailedAnalytics(
  props: NoteGenerationFailedEventProps & {
    generation_trigger?: NoteGenerationTrigger;
    /** Whole-ladder elapsed from Rust; Datadog as `total_elapsed_ms`. */
    duration_ms?: number;
    /** Offline sync-queue attempt count — Datadog facet `attempt_count`. */
    attempt_count?: number | null;
  }
): void {
  // Datadog-only triage fields — keep them off the Snowplow payload so offline
  // dashboards still filter on legacy SESSION_NOTE_TRIGGER_FAILED alone.
  const { transport_error_code, duration_ms, attempt_count, ...snowplowProps } = props;

  track({
    event_name: TrackingEventName.NoteGenerationFailed,
    props: snowplowProps,
    options: noteGenerationAnalyticsOptions,
  });
  mirrorToDatadog(
    TrackingEventName.NoteGenerationFailed,
    toDatadogNoteGenerationAttrs({
      session_id: props.session_id,
      local_session_id: props.local_session_id,
      platform: props.platform,
      device_file_name: props.device_file_name,
      generation_trigger: props.generation_trigger,
      source: props.source,
      duration_ms,
      // Prefer coarse transport code; fall back to Snowplow error_code.
      error_type: transport_error_code ?? props.error_code,
      attempt_count: attempt_count ?? props.attempt_number,
      will_retry: props.will_retry,
    }),
    props.source,
    'warn'
  );
}
